/* eslint-disable max-lines */
import React from 'react';
import ClassCard from 'aquilla/core/organisms/ClassCard';
import ShowCase from 'aquilla/core/molecules/ShowCase';
import styled from '@emotion/styled';
import Image from '@base/FutureImage';
import { mediaQuery, Spacings } from '@styles/index';
import { useRouter } from 'next/router';
import pushRoute from '@utils/pushRoute';
import { getLink, getLinkPath } from '@models/Item';
import { SEE_ALL_CLICKED } from '@constants/segment';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import EducatorThumbnailBackground from 'aquilla/core/atoms/EducatorThumbnailBackground';
import segmentStorage from '@events/segmentStorage';

const PopularClassesContainer = styled.div`
  width: var(--layout-width);
  margin: ${Spacings.SPACING_12B} auto ${Spacings.SPACING_10B};

  ${mediaQuery.uptoTablet} {
    width: 100%;
    margin: ${Spacings.SPACING_8B} auto ${Spacings.SPACING_10B};
  }

  ${mediaQuery.onlyTablet} {
    .aquilla-carousel-wrapper > *:first-child {
      margin-left: ${Spacings.SPACING_8B};
    }
    .aquilla-carousel-wrapper > *:last-child {
      margin-right: ${Spacings.SPACING_8B};
    }
    .showcase-section-header {
      padding: 0 ${Spacings.SPACING_8B};
    }
  }

  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin: ${Spacings.SPACING_8B} auto ${Spacings.SPACING_8B};
    padding: 0;
  }
`;

const ClassThumbnailWrapper = styled.div`
  position: relative;
  width: 272px;
`;

export const EducatorCardThumbnail = ({ coverPhoto, color }) => {
  if (!coverPhoto)
    return (
      <EducatorThumbnailBackground
        variant="content-thumbnail-with-person"
        color={color}
        width={272}
        height={156}
      />
    );
  return (
    <ClassThumbnailWrapper>
      <Image src={coverPhoto} alt="" height={156} width={272} />
    </ClassThumbnailWrapper>
  );
};

export function getClassCard(coverPhoto, color) {
  // eslint-disable-next-line func-names, react/display-name
  return function () {
    return <EducatorCardThumbnail coverPhoto={coverPhoto} color={color} />;
  };
}

const PopularClasses = ({ block }) => {
  const { data: classes, extraBlockInfo } = block;
  const {
    query: { goalSlug, goalUID }
  } = useRouter();
  const goalSegmentData = useGoalEventProps({});
  const handleSeeAll = (e) => {
    e.preventDefault();
    segment.track(SEE_ALL_CLICKED, {
      ...goalSegmentData,
      last_primary_source_section: 'Popular Classes',
      last_primary_source: 'Goal Home'
    });
    const route = {
      href: '/goal/[goalSlug]/[goalUID]/classes',
      as: `/goal/${goalSlug}/${goalUID}/classes`
    };
    pushRoute(route.href, route.as);
  };
  const handleClick = (classInfo, label) => {
    // Adding LPSS to segment from label property and deleting it on class exit
    segmentStorage.setItem('lpss', label);
    const href = getLinkPath();
    const as = getLink({
      ...classInfo,
      nextTrack: {
        ...classInfo.nextSession,
        ...classInfo.nextSession?.properties
      }
    });
    pushRoute(href, as);
  };

  return (
    <PopularClassesContainer>
      <ShowCase
        title={extraBlockInfo.blockHeader}
        itemsPerSlide={4}
        onSeeAll={handleSeeAll}
        hideArrowButtonOnTablet
      >
        {classes.map((classInfo) => {
          if (!classInfo.nextSession) return null;
          return (
            <ClassCard
              as="div"
              key={classInfo.uid}
              title={classInfo.title}
              educatorName={classInfo.educatorName}
              educatorLink={classInfo.educatorLink}
              topicName={classInfo.topicName}
              topicColor={classInfo.topicColor || '#8873FF'}
              langLabel={classInfo.langLabel}
              viewCount={classInfo.viewCount}
              variant={classInfo.variant}
              thumbnail={() => (
                <EducatorCardThumbnail
                  coverPhoto={classInfo.coverPhotoV1}
                  topicColor={classInfo.topicColor}
                />
              )}
              onClick={() => handleClick(classInfo, 'Popular Classes')}
            />
          );
        })}
      </ShowCase>
    </PopularClassesContainer>
  );
};

export default PopularClasses;
